import { useState, useEffect, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import heroImg1 from "@/assets/hero-warehouse-1.webp";
import heroImg2 from "@/assets/hero-warehouse-2.webp";
import heroImg3 from "@/assets/hero-warehouse-3.webp";
import heroImg4 from "@/assets/hero-warehouse-4.webp";

const IMAGES = [heroImg1, heroImg2, heroImg3, heroImg4];
const ALT_TEXTS = [
  "Modern fulfillment warehouse with workers packing orders",
  "AI-powered automated warehouse with robotic systems",
  "Shipping containers and cargo freight operations",
  "FBA prep labeling station with organized inventory",
];
const INTERVAL = 5000;
const FADE_MS = 1500;

const HeroSection = () => {
  const [activeIdx, setActiveIdx] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const scrollTo = (href: string) => {
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  const goTo = useCallback((idx: number) => setActiveIdx(idx), []);

  /* auto-rotate */
  useEffect(() => {
    if (isPaused) return;
    timerRef.current = setInterval(() => {
      setActiveIdx((prev) => (prev + 1) % IMAGES.length);
    }, INTERVAL);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isPaused]);

  /* preload all images */
  useEffect(() => {
    IMAGES.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  return (
    <section
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Background images — stacked with crossfade */}
      <div className="absolute inset-0">
        {IMAGES.map((src, i) => (
          <div
            key={i}
            className="absolute inset-0"
            style={{
              opacity: i === activeIdx ? 1 : 0,
              transition: `opacity ${FADE_MS}ms ease-in-out`,
              ...(i === activeIdx ? { willChange: "opacity" } : {}),
            }}
          >
            <img
              src={src}
              alt={ALT_TEXTS[i]}
              className="w-full h-full object-cover"
              style={{
                transform: i === activeIdx ? "scale(1.05)" : "scale(1)",
                transition: `transform ${INTERVAL}ms ease-out`,
                ...(i === activeIdx ? { willChange: "transform" } : {}),
              }}
              loading={i === 0 ? "eager" : "lazy"}
              {...(i === 0 ? { fetchPriority: "high" as const, decoding: "sync" as const } : { decoding: "async" as const })}
            />
          </div>
        ))}
        {/* Dark overlays — identical to original */}
        <div className="absolute inset-0 bg-navy-deep/70" />
        <div className="absolute inset-0 bg-gradient-to-b from-navy-deep/55 via-navy-deep/30 to-navy-deep/90" />
      </div>

      <div className="relative z-10 container mx-auto px-4 lg:px-8 pt-20 pb-12 text-center max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="inline-flex items-center gap-2 mb-6 px-4 py-1.5 rounded-full border-glow bg-cyan/5 text-cyan text-xs font-medium tracking-wider uppercase">
            <span className="w-1.5 h-1.5 rounded-full bg-cyan animate-glow-pulse" />
            AI-Powered Logistics from Texas
          </div>

          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6 text-primary-foreground">
            Texas-Based, AI-Powered{" "}
            <span className="text-gradient-cyan drop-shadow-[0_0_15px_rgba(6,182,212,0.5)]">End-to-End</span>{" "}
            E-Commerce Fulfillment
          </h1>

          <p className="text-lg sm:text-xl leading-relaxed mb-10 max-w-3xl mx-auto text-primary-foreground/70">
            Leveraging 10 years of e-commerce expertise, TheBigUSA LLC manages your Amazon FBA/FBM,
            Shopify, and eBay operations straight from our Texas hub. We reduce error margins to
            absolute zero using AI technology and seamlessly deliver your products from the USA to the
            world.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => scrollTo("#services")}
              className="bg-cyan text-navy-deep font-semibold hover:bg-cyan-glow rounded-full px-8 py-6 text-base glow-cyan"
            >
              Explore Services
            </Button>
            <Button
              onClick={() => scrollTo("#contact")}
              variant="outline"
              className="rounded-full px-8 py-6 text-base bg-transparent text-primary-foreground border-primary-foreground/40 hover:bg-primary-foreground/10 hover:border-cyan"
            >
              Talk to an Expert
            </Button>
          </div>
        </motion.div>

        {/* Dot indicators */}
        <div className="flex justify-center gap-2.5 mt-10">
          {IMAGES.map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              aria-label={`Go to slide ${i + 1}`}
              className="group p-1"
            >
              <span
                className="block w-2 h-2 rounded-full transition-all duration-300"
                style={{
                  backgroundColor:
                    i === activeIdx
                      ? "hsl(192, 95%, 52%)"
                      : "hsla(210, 20%, 70%, 0.3)",
                  boxShadow:
                    i === activeIdx
                      ? "0 0 8px hsla(192, 95%, 52%, 0.5)"
                      : "none",
                  transform: i === activeIdx ? "scale(1.3)" : "scale(1)",
                }}
              />
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
